/**
 * Settlement player frame — the always-visible corner overlay.
 *
 * ─── Three stacked pieces, not one box (2026-09-15) ─────────────────────────
 * Janeks: *"The inventory, journal, shop and map buttons OUTSIDE of the block,
 * they are shown under it as separate buttons. No background for now. Load,
 * money is in a separate block between the player and the buttons."* So
 * `.bv-hud` is now a transparent, pointer-events-none COLUMN and the panel
 * chrome (background, border, shadow) moved down onto `.bv-hud-panel`, which
 * only the first two children wear:
 *
 *   1. player panel  — portrait + level badge + name + HP/AR/EN + EXP
 *   2. supply panel  — Load and the Cash/Gold wallet
 *   3. action bar    — bare buttons, no panel behind them
 *
 * The same message asked for the level to leave the text line and become a
 * circular badge on the portrait's corner, *"slightly overflowing for depth"*.
 * That badge CANNOT live inside `.bv-hud-portrait`: that box is
 * `overflow: hidden` to clip the WebGL canvas to its rounded corners, so
 * anything overflowing it is cut off at exactly the edge it is supposed to
 * cross. It is a sibling inside `.bv-hud-avatar` instead.
 *
 * Every bar carries its own numbers INSIDE it — HP, AR, EN, EXP and Load alike.
 *
 * The bottom line used to list the window shortcuts ("[I] Record · [J]
 * Journal · …"). Those moved to the action bar (`ui/action-bar.ts`, its own
 * dock in the bottom-left corner since 2026-10-16), where each one is a
 * button that also shows its key —
 * so the line now carries only the walk/run indicator, which is a STATE
 * readout rather than a shortcut list and has nowhere else to live.
 *
 * Plain DOM over the canvas, same pattern as DebugOverlay. Energy ticks via
 * `update()` from the frame loop (throttled — a bar doesn't need 60Hz DOM
 * writes); everything else changes only on a store push, handled by the
 * subscription. The portrait owns its own render loop (see PlayerPortrait).
 *
 * Stat sourcing follows the project's rules exactly (see game-state.ts):
 *  - HP / maxHP from `health` (NOT gear-blended — S3's documented gap)
 *  - energy extrapolated with `character.stats.energyRegenPerMin`, the BASE
 *    rate, because CharacterService computes energy with no equipment input
 *  - armour from `equipment.maxArmor`, shown full: armour is a per-expedition
 *    transient that refills on returning to the settlement (health rule 4), so
 *    there is no lower current value to display here.
 *  - EXP from `character.xpIntoLevel`/`.xpForNextLevel`, both computed SERVER
 *    side. `character.xp` is a lifetime total and the divisor is an env value
 *    (`XP_PER_LEVEL`) this side cannot see, so a bar derived here would be
 *    wrong on any server not running the default — and wrong in a way that
 *    looks entirely correct at level 1.
 */
import { gameStateStore, extrapolateEnergy, refillEnergyPremium } from '../../state/game-state';
import { createHealWatcher } from '../../state/heal-feed';
import { authStore } from '../../../core/state/auth-store';
import { playerDisplayName } from '../render/name-tags';
import { socket } from '../../../core/network/socket';
import { capacityLevel, fillPercent, worstLevel } from './capacity';
import { PlayerPortrait } from './player-portrait';
import { cashCoinHtml } from './coin';
import { hudArtUrl, statEmblemHtml } from './hud-art';
import { openCurrencyShopUI } from './currency-shop-ui';
import { openBankerUI } from './banker-ui';
import { firstBankerKey } from '../../state/banker-state';

/** Same wire shape `vendor-state.ts` receives from this event — {@link VendorBalance}, not imported to avoid a vendor-feature dependency from the always-on HUD. */
interface EconomyBalancePush { cash: number; gold: number }

const ENERGY_TICK_MS = 250;

/**
 * The posture figures — hand-drawn art, built through the item-icon pipeline
 * (`assets/generated_items/build_multisize_icons.py`, sources in
 * `assets/generated_items/ui_out/`) into the same three WebP tiers everything
 * else uses.
 *
 * ─── Silhouette masks, and why (2026-09-16) ────────────────────────────────
 * Janeks: *"RUN AND WALK icons. - Make them match, Walk is filled in white,
 * Run is transparent. Make both of them filled with the same color as text
 * below."*
 *
 * Measured, the three drawings were not the same KIND of drawing: walk had a
 * solid white body (18.8% of the canvas opaque, ~80% bright) while run and
 * crouch were outlines (9.4% / 11.3%). Rendered at the 40 px they are actually
 * drawn at, walk read as a figure and run as a spidery scribble. No CSS colour
 * fixes that.
 *
 * So the art is now consumed as a **silhouette mask**:
 * `assets/generated_items/build_gait_masks.py` fills each figure's interior
 * (flood the border, keep what it cannot reach) and emits
 * `gait_<posture>_mask.webp`, whose ALPHA is the silhouette. The HUD paints it
 * with `background-color: currentColor` through a CSS mask, so the glyph takes
 * the posture colour off `.bv-gait` — the same colour the word under it
 * inherits, from the same declaration.
 *
 * This reverses the old note that lived here ("the figure no longer
 * recolours... the state colour lives on the WORD alone"). It does recolour
 * now, and icon and word can no longer disagree, because neither carries a
 * colour of its own.
 *
 * The original `gait_*_icon*.webp` files are still on disk: they are the
 * SOURCE the mask builder reads. Nothing renders them directly.
 *
 * Globbed rather than imported one by one, matching `paperdoll.ts`'s reasoning
 * and query options exactly — a bare runtime path would 404 in production.
 */
const GAIT_ART_MODULES = import.meta.glob<string>('../assets/ui/gait_*.webp', {
  eager: true, query: '?url', import: 'default',
});
const GAIT_ART: Record<string, string> = {};
for (const [path, url] of Object.entries(GAIT_ART_MODULES)) {
  GAIT_ART[path.slice(path.lastIndexOf('/') + 1)] = url;
}

/**
 * The Gold coin, as real art rather than the `✦` text glyph.
 *
 * In `assets/ui/` beside the gait figures, NOT in `assets/icons/` —
 * `paperdoll.ts` globs that whole directory as the ITEM icon map, and Gold is a
 * currency, not something you can pick up. Built by
 * `assets/generated_items/build_gold_icons.py` into the same three WebP tiers
 * everything else uses; the 64px `_slot` tier is the one a 15px chip wants.
 *
 * Exported so every other Gold readout can use the same mark: the audit found
 * FIVE places drawing Gold as text (hud, shop-ui, currency-shop-ui twice,
 * banker-ui), and changing one leaves the game showing two different Golds.
 */
const GOLD_ART = import.meta.glob<string>('../assets/ui/gold_icon_slot.webp', {
  eager: true, query: '?url', import: 'default',
});

export function goldIconHtml(cssClass = 'bv-gold-icon'): string {
  const url = Object.values(GOLD_ART)[0] ?? '';
  // Decorative: the number beside it is the information, and a screen reader
  // announcing "gold coin 3" reads worse than "3 Gold", which the title gives.
  return url ? `<img class="${cssClass}" src="${url}" alt="" aria-hidden="true" />` : '';
}

/**
 * What the indicator shows. NOT the same axis as `player-controls.ts`'s
 * state: gait (walk/run) and `crouched` are independent there — a player can
 * crouch-walk or crouch-run. The HUD collapses them to one glyph with crouch
 * winning, because crouch is the posture that changes what the player can do
 * (speed, profile), and a two-glyph indicator in the minimap corner is more
 * chrome than the information is worth.
 */
export type GaitPosture = 'walk' | 'run' | 'crouch';

const POSTURE_WORD: Record<GaitPosture, string> = { walk: 'WALK', run: 'RUN', crouch: 'CROUCH' };

/**
 * One posture figure, as a mask-painted span. Pure.
 *
 * Not an `<img>`: an image paints its own pixels and cannot take the
 * surrounding text colour. The mask is carried on a custom property rather
 * than a class so the hashed asset URL (Vite rewrites it at build time) stays
 * out of the stylesheet — `styles.ts` is a static string and cannot see it.
 *
 * One file per posture, no `srcset`: a filled silhouette at 128 px is ~2 KB
 * and has no fine detail that a larger source would preserve.
 */
export function gaitIconHtml(posture: GaitPosture): string {
  const mask = GAIT_ART[`gait_${posture}_mask.webp`] ?? '';
  return `<span class="bv-gait-glyph" style="--gait-mask:url(${mask})" aria-hidden="true"></span>`;
}

function errorMessage(err: unknown): string {
  return err instanceof Error && err.message ? err.message : 'Action failed';
}

export class Hud {
  readonly el: HTMLDivElement;
  /**
   * Where the arena's "pending + timer" badge mounts.
   *
   * Janeks: *"waiting for matchmaking is shown as 'pending + timer' under the
   * menus"* — so it sits in normal flow inside `.bv-hud`, under the player
   * blocks (the action bar it used to sit under moved to the bottom-left
   * corner on 2026-10-16). That placement is the whole reason it is a host on the HUD and
   * not a free-floating overlay: `.bv-hud` is the one column that renders
   * ABOVE the full-screen panel scrims (z-index 1002 vs 1001), so the badge
   * stays visible and its Cancel button stays clickable with the inventory or
   * the map open on top of everything else.
   *
   * The HUD provides the slot, `index.ts` owns what goes in it, so an always-mounted overlay does not end up importing
   * the arena.
   */
  readonly arenaStatusHost: HTMLElement;
  /**
   * The walk/run indicator, beside the minimap.
   *
   * A SECOND root, not a child of `el`: `.bv-hud` is `position:absolute` in
   * the top-left corner, so anything positioned inside it anchors to that box
   * rather than to the viewport. It is owned by this class anyway, because
   * `update(now, gait)` is where the gait arrives.
   *
   * `index.ts` hides it alongside the minimap — see the frame loop.
   */
  readonly gaitEl: HTMLDivElement;
  private readonly unsub: () => void;
  private readonly unsubAuth: () => void;
  private readonly portrait = new PlayerPortrait();
  private lastTick = 0;
  /** Last gait painted into the indicator — avoids a DOM write when unchanged. */
  private lastGait: GaitPosture | null = null;
  private msgTimeout: number | undefined;
  /**
   * Detects the HEAL EDGE — see `state/heal-feed.ts` for the several things
   * that look like a heal and are not (a bigger max HP, a Med-Center respawn,
   * the very first reading).
   *
   * Not a width diff inside `setBar`: that method repaints on every tick, so a
   * width comparison would have to distinguish "the bar moved" from "the bar
   * moved UP for a reason worth celebrating", which is exactly what the
   * watcher already answers.
   */
  private readonly healWatcher = createHealWatcher();
  private healTimeout: number | undefined;

  constructor(parent: HTMLElement) {
    this.el = document.createElement('div');
    this.el.className = 'bv-hud';
    // Art URLs are hashed at build time, so they reach the stylesheet as custom
    // properties rather than being written into it.
    for (const [prop, art] of [['--hud-plate', 'hud_plate'], ['--hud-medal', 'lvl_medal'], ['--hud-grain', 'bar_grain'], ['--hud-btn', 'btn_plate']]) {
      const url = hudArtUrl(art);
      if (url) this.el.style.setProperty(prop, `url(${url})`);
    }
    this.el.innerHTML = `
      <div class="bv-hud-panel">
        <div class="bv-hud-main">
          <div class="bv-hud-avatar">
            <div class="bv-hud-portrait" data-portrait></div>
            <span class="bv-hud-level-badge" data-level title="Level">—</span>
          </div>
          <div class="bv-hud-bars">
            <div class="bv-hud-name" data-player-name>—</div>
            <div class="bv-hud-row">
              ${statEmblemHtml('hp', 'HP')}
              <div class="bv-hud-bar">
                <div class="bv-hud-fill bv-hud-fill-hp" data-hp-fill></div>
                <span class="bv-hud-inbar" data-hp-text>—</span>
              </div>
            </div>
            <div class="bv-hud-row">
              ${statEmblemHtml('ar', 'AR')}
              <div class="bv-hud-bar">
                <div class="bv-hud-fill bv-hud-fill-ar" data-ar-fill></div>
                <span class="bv-hud-inbar" data-ar-text>—</span>
              </div>
            </div>
            <div class="bv-hud-row">
              ${statEmblemHtml('en', 'EN')}
              <div class="bv-hud-bar">
                <div class="bv-hud-fill bv-hud-fill-en" data-en-fill></div>
                <span class="bv-hud-inbar" data-en-text>—</span>
              </div>
              <button type="button" class="bv-hud-mini-btn" data-refill-energy title="Refill energy with Gold">⚡</button>
            </div>
          </div>
        </div>
        <div class="bv-hud-row bv-hud-row-xp">
          ${statEmblemHtml('xp', 'EXP')}
          <div class="bv-hud-bar bv-hud-bar-xp">
            <div class="bv-hud-fill bv-hud-fill-xp" data-xp-fill></div>
            <span class="bv-hud-inbar" data-xp-text>— EXP</span>
          </div>
        </div>
        <div class="bv-hud-tape" aria-hidden="true">
          <span class="bv-pd-tape bv-hud-tape-stuck"></span>
          <span class="bv-hud-tape-fall"><span class="bv-pd-tape bv-pd-tape-loose"></span></span>
        </div>
      </div>
      <div class="bv-hud-panel bv-hud-supply" data-supply hidden>
        <div class="bv-hud-carry" data-carry hidden>
          <div class="bv-hud-carry-head">
            <span class="bv-hud-label">LOAD</span>
            <span class="bv-hud-carry-slots" data-slots></span>
          </div>
          <div class="bv-hud-bar bv-hud-bar-carry">
            <div class="bv-hud-carry-fill" data-weight-fill></div>
            <span class="bv-hud-inbar" data-weight>—</span>
          </div>
        </div>
        <div class="bv-hud-wallet" data-wallet hidden>
          <button type="button" class="bv-hud-chip bv-hud-chip-cash" data-cash-chip
            title="Exchange Gold for Cash">
            <span class="bv-hud-chip-mark">${cashCoinHtml('sm')}</span><span data-cash>—</span>
          </button>
          <button type="button" class="bv-hud-chip bv-hud-chip-gold" data-gold-chip
            title="Buy more Gold">
            ${goldIconHtml('bv-hud-chip-coin')}<span data-gold>—</span>
          </button>
        </div>
      </div>
      <div data-arena-status></div>
      <div class="bv-hud-msg" data-hud-msg hidden></div>`;
    parent.appendChild(this.el);

    // Was a word in the HUD's own bottom-left corner, which put it as far from
    // the player's eye as the layout allows. It now lives beside the minimap,
    // where the player is already looking to orient.
    this.gaitEl = document.createElement('div');
    this.gaitEl.className = 'bv-gait';
    this.gaitEl.innerHTML = `
      <div class="bv-gait-icon" data-gait-icon>${gaitIconHtml('run')}</div>
      <div class="bv-gait-word" data-gait>RUN</div>`;
    parent.appendChild(this.gaitEl);
    this.arenaStatusHost = this.el.querySelector('[data-arena-status]') as HTMLElement;

    const portraitHost = this.el.querySelector('[data-portrait]') as HTMLElement | null;
    if (portraitHost) this.portrait.mount(portraitHost);

    // The NUMBERS are the buttons now, not a 16px `+` beside them. Janeks:
    // *"Show Cash and Gold amount in Load section. Make it larger and looking
    // like labels/buttons, press on Gold -> shop, topup., press on cash -> Gold
    // to Cash."*
    this.el.querySelector('[data-gold-chip]')?.addEventListener('click', () => openCurrencyShopUI());
    this.el.querySelector('[data-cash-chip]')?.addEventListener('click', () => this.onExchange());
    this.el.querySelector('[data-refill-energy]')?.addEventListener('click', () => this.onRefillEnergy());

    // `subscribe` fires immediately with the current state (see core/state/store.ts),
    // so this doubles as the initial render.
    this.unsub = gameStateStore.subscribe(() => this.render());
    // The name is the ACCOUNT's, not the character sheet's, so it comes from a
    // different store — the same one and the same resolver the world name tag
    // over the player's head uses (`playerDisplayName`), so the two can never
    // disagree about what the player is called.
    this.unsubAuth = authStore.subscribe(() => {
      this.set('[data-player-name]', playerDisplayName(authStore.get().user));
    });

    // Character sheet gives the value on load/refetch; this keeps it live in
    // between without needing a vendor panel open — vendor-state.ts starts
    // the identical listener but only while ITS panel is open. The HUD is
    // always mounted, so it owns its own subscription for its own lifetime.
    socket.on('economy:updated', this.onBalancePush);
  }

  private readonly onBalancePush = (balance: EconomyBalancePush): void => {
    this.renderWallet(balance.cash, balance.gold);
  };

  /**
   * Instant full energy refill for Gold. No confirm step — a single
   * fixed-cost action, same "just spend" convention as paperdoll's
   * Equip/Store/Discard buttons; the confirm-before-committing UX is
   * reserved for the Banker's currency exchange (banker-ui.ts), which moves
   * a much larger amount and has no undo.
   */
  /**
   * Cash chip -> the Gold->Cash exchange.
   *
   * The exchange is one-way by design (crafting-economy.md rule 12: "Gold is
   * deposit-only, no gameplay faucet"), so "press on cash -> Gold to Cash" maps
   * onto the banker panel that already exists — the only thing missing was an
   * entry point that did not require walking to Halvard.
   *
   * The key is RESOLVED from `GET /banker`, never hardcoded. A literal
   * `'halvard'` would compile, work today, and go silently dead the day the
   * seed renames him — this repo's recorded "hardcoded constants hide
   * unreachable features" trap. An empty roster shows a message instead of
   * opening a panel that cannot load.
   */
  private onExchange(): void {
    void firstBankerKey().then((key) => {
      if (!key) { this.showMessage('No moneychanger is trading right now.'); return; }
      openBankerUI(key);
    });
  }

  private onRefillEnergy(): void {
    refillEnergyPremium().then(
      (result) => this.showMessage(`Energy refilled for ${result.cost} Gold.`),
      (err) => this.showMessage(errorMessage(err)),
    );
  }

  private showMessage(text: string): void {
    const el = this.el.querySelector('[data-hud-msg]') as HTMLElement | null;
    if (!el) return;
    el.hidden = false;
    el.textContent = text;
    window.clearTimeout(this.msgTimeout);
    this.msgTimeout = window.setTimeout(() => { el.hidden = true; }, 4000);
  }

  /**
   * Called every render frame; the stat bars only touch the DOM every
   * ENERGY_TICK_MS, but the posture (when supplied) updates immediately on
   * change — the walk/run/crouch toggle should read correctly the same frame
   * it flips, not lag behind the energy-bar throttle. Both args are optional
   * so this stays callable from anywhere that doesn't track them.
   *
   * `crouched` WINS over `gait`: see {@link GaitPosture} for why the two
   * independent control-scheme states collapse into one glyph.
   */
  update(now: number, gait?: 'walk' | 'run', crouched?: boolean): void {
    const posture: GaitPosture | undefined = crouched ? 'crouch' : gait;
    if (posture && posture !== this.lastGait) {
      this.lastGait = posture;
      const word = this.gaitEl.querySelector('[data-gait]');
      if (word) word.textContent = POSTURE_WORD[posture];
      const icon = this.gaitEl.querySelector('[data-gait-icon]');
      if (icon) icon.innerHTML = gaitIconHtml(posture);
      // Both the word AND the masked glyph read their colour from these
      // classes — the glyph through `currentColor` (see gaitIconHtml), so the
      // two can no longer disagree. These classes now drive
      // the WORD only — but they still must never disagree with the figure.
      this.gaitEl.classList.toggle('bv-gait-walk', posture === 'walk');
      this.gaitEl.classList.toggle('bv-gait-crouch', posture === 'crouch');
    }
    if (now - this.lastTick < ENERGY_TICK_MS) return;
    this.lastTick = now;
    this.render();
  }

  private render(): void {
    const {
      character, health, equipment, inventory, characterFetchedAt,
    } = gameStateStore.get();

    if (character?.state === 'ACTIVE') {
      this.set('[data-level]', `${character.level}`);
      this.setBar(
        '[data-xp-fill]', '[data-xp-text]', character.xpIntoLevel, character.xpForNextLevel, 'EXP',
      );
      const energy = extrapolateEnergy(
        character.energy, character.stats.energyRegenPerMin, character.maxEnergy, characterFetchedAt, Date.now(),
        character.energyBoost,
      );
      this.setBar('[data-en-fill]', '[data-en-text]', energy, character.maxEnergy);
      this.renderWallet(character.softBalance, character.premiumBalance);
    }

    if (health?.state === 'ACTIVE') {
      // BEFORE `setBar`: the class has to be on the element when the width
      // changes, or the browser animates the new width under the old
      // transition and the heal sweep is simply never seen.
      if (this.healWatcher(health)) this.flashHeal();
      this.setBar('[data-hp-fill]', '[data-hp-text]', health.hp, health.maxHP);
    }

    this.renderArmor(equipment?.maxArmor);
    this.renderCarry(inventory);
  }

  /**
   * `maxArmor === 0` is the real state for an unarmoured survivor (base armour
   * from no gear is 0), so it gets an explicit dim "none" rather than looking
   * like data that failed to arrive. `undefined` means the sheet has not
   * loaded yet and keeps the placeholder dash.
   */
  private renderArmor(maxArmor: number | undefined): void {
    if (maxArmor === undefined) return;
    const fill = this.el.querySelector('[data-ar-fill]') as HTMLElement | null;
    const unarmoured = maxArmor <= 0;

    if (fill) fill.style.width = unarmoured ? '0%' : '100%';
    fill?.parentElement?.classList.toggle('bv-hud-bar-empty', unarmoured);
    this.set('[data-ar-text]', unarmoured ? 'none' : `${Math.round(maxArmor)}`);
  }

  /**
   * Weight AND slots, both styled by pressure: inventory.md rule 3 blocks the
   * carried bag on whichever cap hits first, so colouring weight alone would
   * mislead a player who is actually slot-blocked. The bar tracks weight (the
   * continuous cap); its colour is the worse of the two.
   */
  private renderCarry(inventory: ReturnType<typeof gameStateStore.get>['inventory']): void {
    const box = this.el.querySelector('[data-carry]') as HTMLElement | null;
    if (!box) return;
    if (!inventory) {
      box.hidden = true;
      this.syncSupplyPanel();
      return;
    }
    box.hidden = false;
    this.syncSupplyPanel();

    const weightLevel = capacityLevel(inventory.carriedWeight, inventory.carryWeightCap);
    const slotLevel = capacityLevel(inventory.carriedSlotCount, inventory.carriedSlotCap);
    const level = worstLevel(weightLevel, slotLevel);

    box.classList.remove('bv-cap-ok', 'bv-cap-warn', 'bv-cap-critical');
    box.classList.add(`bv-cap-${level}`);

    // No spaces around the slash: this now sits INSIDE a 15px bar roughly
    // 200px wide, and "12.0 / 12.0 kg" is the widest thing the HUD prints.
    this.set('[data-weight]', `${inventory.carriedWeight.toFixed(1)}/${inventory.carryWeightCap.toFixed(1)} kg`);
    this.set('[data-slots]', `${inventory.carriedSlotCount}/${inventory.carriedSlotCap} slots`);
    const fill = this.el.querySelector('[data-weight-fill]') as HTMLElement | null;
    if (fill) fill.style.width = `${fillPercent(inventory.carriedWeight, inventory.carryWeightCap)}%`;
  }

  private renderWallet(cash: number, gold: number): void {
    const box = this.el.querySelector('[data-wallet]') as HTMLElement | null;
    if (box) box.hidden = false;
    // Bare numbers: the chip draws the mark (a `¢` for Cash, the coin art for
    // Gold), so appending a glyph here would print it twice.
    this.set('[data-cash]', `${cash}`);
    this.set('[data-gold]', `${gold}`);
    this.syncSupplyPanel();
  }

  /**
   * The Load and wallet rows arrive independently (inventory sheet vs.
   * character sheet / economy push) and each hides itself until its own data
   * lands. Their shared panel has to hide while BOTH are empty, or the first
   * paint shows an empty bordered box that fills in a moment later — read as a
   * flicker, not as loading.
   */
  private syncSupplyPanel(): void {
    const panel = this.el.querySelector('[data-supply]') as HTMLElement | null;
    if (!panel) return;
    const carry = this.el.querySelector('[data-carry]') as HTMLElement | null;
    const wallet = this.el.querySelector('[data-wallet]') as HTMLElement | null;
    panel.hidden = (carry?.hidden ?? true) && (wallet?.hidden ?? true);
  }

  private setBar(
    fillSel: string,
    textSel: string,
    value: number,
    max: number,
    /** Appended after the numbers, e.g. "40/100 EXP". Omitted for the stat bars. */
    suffix?: string,
  ): void {
    const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
    const fill = this.el.querySelector(fillSel) as HTMLElement | null;
    if (fill) fill.style.width = `${pct}%`;
    const text = `${Math.round(value)}/${Math.round(max)}`;
    this.set(textSel, suffix ? `${text} ${suffix}` : text);
  }

  /**
   * Runs the slow bright sweep on the HP bar, then puts it back.
   *
   * The class is always removed again: left on, the next hit would DRAIN at
   * heal speed, which reads as the game failing to register the damage. The
   * timer is restarted rather than stacked, so regen ticking several times in a
   * row is one continuous sweep instead of a flicker.
   */
  private flashHeal(): void {
    const fill = this.el.querySelector('[data-hp-fill]') as HTMLElement | null;
    if (!fill) return;
    window.clearTimeout(this.healTimeout);
    fill.classList.add('is-healing');
    // Matches the CSS duration. A shorter timeout would strip the class
    // mid-sweep and snap the bar to its new width.
    this.healTimeout = window.setTimeout(() => fill.classList.remove('is-healing'), 800);
  }

  private set(sel: string, text: string): void {
    const node = this.el.querySelector(sel);
    if (node) node.textContent = text;
  }

  dispose(): void {
    this.unsub();
    this.unsubAuth();
    socket.off('economy:updated', this.onBalancePush);
    window.clearTimeout(this.msgTimeout);
    window.clearTimeout(this.healTimeout);
    this.portrait.dispose();
    this.el.remove();
    this.gaitEl.remove();
  }
}
